/**
 * Internal-source attribution for on-site discovery → PDP journeys.
 *
 * The peptide-calculator and the search results (and later quiz / stack-builder) link to
 * product pages. To answer "do calculator/search users buy more?" as a property-filtered
 * funnel (not a fragile same-person path query), the PDP stamps the surface that sent the
 * visitor on its `product_viewed` event AND on the add to cart the visitor makes there.
 * This is intentionally scoped to the one PDP the link pointed to (NOT a PostHog
 * super-property), so later organic browsing in the same session is never attributed.
 *
 * The links themselves stay CLEAN (`/de/eu/products/ghk-cu`, plus `?variant=` where the
 * link preselects a variant): crawlers must not discover parameter URLs. GSC finding
 * 16 Oct 2026: hundreds of `?src=calculator&src_slug=…` product URLs had impressions and
 * some were chosen as their own canonical. Instead, the click hands the attribution over
 * in `sessionStorage` (`rememberInternalSource`, wired up by `InternalSourceLink`), keyed
 * by the target pathname and valid for 30 minutes in the same tab. The PDP reads it with
 * `readInternalSource` and does not delete it, so the add-to-cart that follows (also after
 * a dose or pack switch, which only changes the query) sees the same source.
 *
 * Analytics semantics from the deploy of this handoff on (branch
 * feat/seo-audit-massnahmen-20261016, 17 Oct 2026); earlier events carried `?src=` in the URL:
 * - `$current_url` / `$pathname` filters on `src=calculator` (or any `src=`) stop matching new
 *   visits. Use `internal_source` / `internal_source_detail` on `product_viewed` and on the
 *   cart events (`product_add_to_cart_attempted`, `product_added_to_cart`,
 *   `product_add_to_cart_failed`) instead.
 * - The 30 minutes count from the click, not from the page view: an add more than 30 minutes
 *   after the click, even without leaving the PDP, falls back to `pdp`.
 * - A revisit of the same PDP in the same tab within 30 minutes of the click inherits the
 *   earlier source.
 * - Modifier or new-tab clicks (cmd/ctrl/shift/alt, middle click, `target` other than
 *   `_self`) store no handoff, so those visits are unattributed (sessionStorage belongs to a
 *   tab).
 * - Clicks before hydration store no handoff either: until React attaches the click handler
 *   the link is plain HTML. The server-rendered spoke hero CTA on the calculator pages is the
 *   most exposed link.
 *
 * Legacy links are still READ, never written: `#src=…` fragments and `?src=…&src_slug=…`
 * queries from bookmarks, external links and already indexed URLs. The server never sees
 * the handoff: the PDP forwards the validated source in the cart payload.
 */

const INTERNAL_SOURCE_PARAM = "src";
const INTERNAL_SOURCE_DETAIL_PARAM = "src_slug";

/**
 * Surfaces a deep-link may claim as the origin of a PDP visit. `pdp` and `home` are
 * deliberately NOT among them — those are stamped by the page itself on an add when no
 * deep-link referred the visit. The cart-side vocabulary `CartInternalSource`
 * (`@/lib/analytics/add-to-cart-events`) is therefore this list plus `pdp` and `home`.
 */
const INTERNAL_SOURCES = ["calculator", "quiz", "search", "stack-builder"] as const;

export type InternalSource = (typeof INTERNAL_SOURCES)[number];

const INTERNAL_SOURCE_SET = new Set<string>(INTERNAL_SOURCES);

/**
 * Shape accepted as `src_slug`: a peptide slug (`ss-31`) or a search submission id (UUID).
 * NEVER a free-text search term — the detail is joined into analytics properties, so it
 * has to stay a machine id. Shared with the server-side cart attribution parser so both
 * ends of the journey agree on what is safe.
 */
const INTERNAL_SOURCE_DETAIL_PATTERN = /^[a-z0-9][a-z0-9_-]{0,79}$/i;

export function isSafeInternalSourceDetail(value: unknown): value is string {
	return typeof value === "string" && INTERNAL_SOURCE_DETAIL_PATTERN.test(value);
}

export interface InternalSourceAttribution {
	internal_source?: InternalSource;
	internal_source_detail?: string;
}

/** sessionStorage key of the click → PDP handoff. */
export const INTERNAL_SOURCE_STORAGE_KEY = "bb_internal_source";

/** How long a click hands its attribution to the PDP it pointed to. */
export const INTERNAL_SOURCE_HANDOFF_TTL_MS = 30 * 60 * 1000;

/** What a click stores for the page it points to. */
interface InternalSourceHandoff {
	path: string;
	source: InternalSource;
	detail?: string;
	at: number;
}

/** Validate a source/detail pair from any visitor-controlled carrier. */
function toAttribution(source: unknown, detail: unknown): InternalSourceAttribution {
	if (typeof source !== "string" || !INTERNAL_SOURCE_SET.has(source)) return {};
	return {
		internal_source: source as InternalSource,
		internal_source_detail: isSafeInternalSourceDetail(detail) ? detail : undefined,
	};
}

function parseInternalSourceParams(params: URLSearchParams): InternalSourceAttribution {
	return toAttribution(params.get(INTERNAL_SOURCE_PARAM), params.get(INTERNAL_SOURCE_DETAIL_PARAM));
}

/**
 * Hand the attribution of a clicked link over to the page it points to. Call it on click,
 * right before the navigation; the link's href stays clean. Storage that is blocked (private
 * mode, disabled site data) or full must never break the navigation, so every failure just
 * leaves the visit unattributed.
 */
export function rememberInternalSource(
	targetHref: string,
	source: InternalSource,
	detail?: string | null,
	now = Date.now(),
): void {
	const attribution = toAttribution(source, detail);
	if (!attribution.internal_source) return;
	try {
		const handoff: InternalSourceHandoff = {
			path: new URL(targetHref, "https://internal-source.invalid").pathname,
			source: attribution.internal_source,
			detail: attribution.internal_source_detail,
			at: now,
		};
		globalThis.sessionStorage.setItem(INTERNAL_SOURCE_STORAGE_KEY, JSON.stringify(handoff));
	} catch {
		// No storage, no attribution; the navigation itself is unaffected.
	}
}

function readHandoff(pathname: string, now: number): InternalSourceAttribution {
	let stored: unknown;
	try {
		const raw = globalThis.sessionStorage.getItem(INTERNAL_SOURCE_STORAGE_KEY);
		if (!raw) return {};
		stored = JSON.parse(raw);
	} catch {
		return {};
	}
	if (!stored || typeof stored !== "object") return {};
	const { path, source, detail, at } = stored as Record<string, unknown>;
	if (path !== pathname || typeof at !== "number") return {};
	// A timestamp from the future is forged (or the clock jumped back) and would never expire.
	const age = now - at;
	if (!(age >= 0 && age <= INTERNAL_SOURCE_HANDOFF_TTL_MS)) return {};
	return toAttribution(source, detail);
}

/**
 * Read the internal-source attribution of the current PDP (client-side), in this order:
 * (a) the same-tab click handoff when `pathname` is the page it pointed to and it is at most
 * 30 minutes old, (b) a legacy `#src=…` fragment, (c) a legacy `?src=…` query. Each carrier
 * is read as a whole, so a source from one never pairs with a detail from another.
 * Every value is validated here: storage and URL are visitor-controlled, so an unknown
 * source or a forged detail must never reach an analytics property.
 */
export function readInternalSource(
	search: string,
	hash = "",
	pathname?: string,
	now = Date.now(),
): InternalSourceAttribution {
	if (pathname) {
		const fromHandoff = readHandoff(pathname, now);
		if (fromHandoff.internal_source) return fromHandoff;
	}
	const fromFragment = parseInternalSourceParams(new URLSearchParams(hash.replace(/^#/, "")));
	if (fromFragment.internal_source) return fromFragment;
	return parseInternalSourceParams(new URLSearchParams(search));
}
